import React, { useState, useEffect, useCallback } from 'react';
import { adminFetch } from '../../api/client';

const API_URL = process.env.REACT_APP_API_URL || '';

const formatTimeAgo = (iso) => {
    if (!iso) return 'never';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
};

const formatWeekRange = (weekStart) => {
    if (!weekStart) return '—';
    const start = new Date(weekStart + 'T00:00:00');
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const opts = { month: 'short', day: 'numeric' };
    return `${start.toLocaleDateString('en-US', opts)} — ${end.toLocaleDateString('en-US', opts)}, ${start.getFullYear()}`;
};

const StatusBadge = ({ value }) => {
    if (!value) return <span className="admin2-badge admin2-badge-neutral">—</span>;
    const v = value.toUpperCase();
    if (v === 'SUCCESS' || v === 'SENT') return <span className="admin2-badge admin2-badge-success">{value}</span>;
    if (v === 'FAILED' || v === 'FAIL') return <span className="admin2-badge admin2-badge-error">{value}</span>;
    if (v === 'PENDING' || v === 'PARTIAL') return <span className="admin2-badge admin2-badge-warning">{value}</span>;
    return <span className="admin2-badge admin2-badge-neutral">{value}</span>;
};

const NotificationBadge = ({ value }) => {
    if (!value) return <span className="admin2-badge admin2-badge-neutral">—</span>;
    const v = value.toLowerCase();
    if (v === 'sent') return <span className="admin2-badge admin2-badge-success">Sent</span>;
    if (v === 'pending') return <span className="admin2-badge admin2-badge-warning">Pending</span>;
    if (v === 'partial') return <span className="admin2-badge admin2-badge-error">Partial</span>;
    return <span className="admin2-badge admin2-badge-neutral">{value}</span>;
};

const DashboardTab = ({ user }) => {
    const [data, setData] = useState(null);
    const [scriptRuns, setScriptRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [unmatchedExpanded, setUnmatchedExpanded] = useState(false);
    const [secondsAgo, setSecondsAgo] = useState(0);


    const fetchData = useCallback(async () => {
        try {
            const [summaryRes, runsRes] = await Promise.all([
                adminFetch(`${API_URL}/api/admin/weekly-summary`),
                adminFetch(`${API_URL}/api/admin/script-runs?hours=24`),
            ]);

            if (!summaryRes.ok) throw new Error(`Weekly summary failed: ${summaryRes.status}`);
            if (!runsRes.ok) throw new Error(`Script runs failed: ${runsRes.status}`);

            const summaryJson = await summaryRes.json();
            const runsJson = await runsRes.json();

            if (!summaryJson.success) throw new Error(summaryJson.error || 'Summary request failed');

            setData(summaryJson);
            setScriptRuns(runsJson.success ? runsJson.runs : []);
            setLastUpdated(Date.now());
            setSecondsAgo(0);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    // Initial fetch + 30-second auto-refresh, paused when tab hidden
    useEffect(() => {
        fetchData();

        const interval = setInterval(() => {
            if (!document.hidden) fetchData();
        }, 30000);

        return () => clearInterval(interval);
    }, [fetchData]);

    // "Last updated Xs ago" ticker
    useEffect(() => {
        if (!lastUpdated) return;
        const ticker = setInterval(() => {
            setSecondsAgo(Math.floor((Date.now() - lastUpdated) / 1000));
        }, 1000);
        return () => clearInterval(ticker);
    }, [lastUpdated]);

    if (loading) {
        return <div className="admin2-loading-inline">Loading dashboard...</div>;
    }

    if (error) {
        return (
            <div className="admin2-error-banner">
                <strong>Error:</strong> {error}
                <button className="admin2-btn admin2-btn-sm admin2-btn-secondary" onClick={fetchData}>
                    Retry
                </button>
            </div>
        );
    }

    const matching = data?.matching || {};
    const matches = data?.matches || [];
    const unmatched = data?.unmatched || [];
    const notifications = data?.notifications || {};
    const jobs = data?.jobs || {};
    const communityMatches = matching.communityMatches || {};
    const communityEntries = Object.entries(communityMatches);

    // Community matches first, then global
    const sortedMatches = [...matches].sort((a, b) => {
        const aIsGlobal = !a.pool || a.pool === 'Global';
        const bIsGlobal = !b.pool || b.pool === 'Global';
        if (aIsGlobal && !bIsGlobal) return 1;
        if (!aIsGlobal && bIsGlobal) return -1;
        return 0;
    });

    const totalUsersMatched = matches.length * 2;
    const lastUpdatedText = secondsAgo < 5 ? 'just now' : `${secondsAgo}s ago`;

    return (
        <div className="admin2-dashboard">
            {/* Toolbar */}
            <div className="admin2-toolbar">
                <span className="admin2-last-updated">Last updated: {lastUpdatedText}</span>
                <button className="admin2-btn admin2-btn-sm admin2-btn-secondary" onClick={fetchData}>
                    Refresh
                </button>
            </div>

            {/* Row 1: This Week + Notifications */}
            <div className="admin2-grid">
                {/* Card: This Week */}
                <div className="admin2-card">
                    <h3 className="admin2-card-title">This Week</h3>
                    <p className="admin2-card-subtitle">{formatWeekRange(data?.weekStart)}</p>

                    <div className="admin2-card-row">
                        <span className="admin2-label">Matching status</span>
                        <StatusBadge value={matching.status} />
                    </div>
                    <div className="admin2-card-row">
                        <span className="admin2-label">Last run</span>
                        <span className="admin2-value">{formatTimeAgo(matching.lastRun)}</span>
                    </div>

                    <div className="admin2-metric-block">
                        <span className="admin2-metric">{(matching.globalMatches || 0) + communityEntries.reduce((sum, [, v]) => sum + v, 0)}</span>
                        <span className="admin2-metric-label">total matches</span>
                    </div>
                </div>

                {/* Card: Notifications */}
                <div className="admin2-card">
                    <h3 className="admin2-card-title">Notifications</h3>

                    <div className="admin2-card-row">
                        <span className="admin2-label">Sent</span>
                        <span className="admin2-value admin2-value-success">{notifications.sent ?? 0}</span>
                    </div>
                    <div className="admin2-card-row">
                        <span className="admin2-label">Pending</span>
                        <span className={`admin2-value${notifications.pending > 0 ? ' admin2-value-warning' : ''}`}>
                            {notifications.pending ?? 0}
                        </span>
                    </div>
                    <div className="admin2-card-row">
                        <span className="admin2-label">Failed</span>
                        <span className={`admin2-value${notifications.partial > 0 ? ' admin2-value-error' : ''}`}>
                            {notifications.partial ?? 0}
                        </span>
                    </div>

                    <div className="admin2-card-divider" />

                    <div className="admin2-card-row">
                        <span className="admin2-label">Job status</span>
                        <StatusBadge value={jobs.notifications?.lastStatus} />
                    </div>
                    <div className="admin2-card-row">
                        <span className="admin2-label">Last run</span>
                        <span className="admin2-value">{formatTimeAgo(jobs.notifications?.lastRun)}</span>
                    </div>
                </div>
            </div>

            {/* Row 2: Matches Breakdown + Unmatched Users */}
            <div className="admin2-grid">
                {/* Card: Matches Breakdown */}
                <div className="admin2-card">
                    <h3 className="admin2-card-title">Matches Breakdown</h3>

                    <div className="admin2-card-row">
                        <span className="admin2-label">Global</span>
                        <span className="admin2-value">{matching.globalMatches ?? 0}</span>
                    </div>

                    {communityEntries.length > 0 ? (
                        communityEntries.map(([slug, count]) => (
                            <div className="admin2-card-row" key={slug}>
                                <span className="admin2-label">{slug}</span>
                                <span className="admin2-value">{count}</span>
                            </div>
                        ))
                    ) : (
                        <p className="admin2-muted">No community matches</p>
                    )}

                    <div className="admin2-card-divider" />
                    <div className="admin2-card-row">
                        <span className="admin2-label">Users matched</span>
                        <span className="admin2-value">{totalUsersMatched}</span>
                    </div>
                </div>

                {/* Card: Unmatched Users */}
                <div className="admin2-card">
                    <h3 className="admin2-card-title">Unmatched Users</h3>

                    {unmatched.length === 0 ? (
                        <p className="admin2-all-matched" style={{ color: '#16a34a' }}>
                            All active users matched!
                        </p>
                    ) : (
                        <>
                            <div className="admin2-metric-block">
                                <span className="admin2-metric">{unmatched.length}</span>
                                <span className="admin2-metric-label">unmatched</span>
                            </div>
                            <button
                                className="admin2-btn admin2-btn-sm admin2-btn-ghost"
                                onClick={() => setUnmatchedExpanded((prev) => !prev)}
                            >
                                {unmatchedExpanded ? 'Hide list' : 'Show list'}
                            </button>
                            {unmatchedExpanded && (
                                <ul className="admin2-unmatched-list">
                                    {unmatched.map((u) => (
                                        <li key={u.username} className="admin2-unmatched-item">
                                            <div>
                                                <span className="admin2-unmatched-name">{u.name}</span>
                                                <span className="admin2-muted"> @{u.username}</span>
                                            </div>
                                            {u.reason && (
                                                <div className="admin2-unmatched-reason">{u.reason}</div>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Full-width: Engagement Stats */}
            {data.engagement && (
                <div className="admin2-card admin2-card-full">
                    <h3 className="admin2-card-title">Engagement</h3>
                    <div className="admin2-engagement-grid">
                        {/* Meeting Status */}
                        <div className="admin2-engagement-section">
                            <h4 className="admin2-engagement-subtitle">Meeting Status</h4>
                            <div className="admin2-engagement-response-rate">
                                Response rate: <strong>{data.engagement.responseRate}%</strong>
                            </div>
                            {Object.entries(data.engagement.meetingStatus)
                                .sort(([,a], [,b]) => b - a)
                                .map(([status, count]) => {
                                    const total = Object.values(data.engagement.meetingStatus).reduce((a, b) => a + b, 0);
                                    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                                    const colors = {
                                        'Met': '#22c55e', 'Scheduled': '#3b82f6', 'No': '#f59e0b',
                                        'Fail': '#ef4444', 'No response': '#cbd5e1'
                                    };
                                    return (
                                        <div key={status} className="admin2-engagement-bar-row">
                                            <span className="admin2-engagement-label">{status}</span>
                                            <div className="admin2-engagement-bar-bg">
                                                <div
                                                    className="admin2-engagement-bar-fill"
                                                    style={{ width: `${pct}%`, backgroundColor: colors[status] || '#94a3b8' }}
                                                />
                                            </div>
                                            <span className="admin2-engagement-count">{count} ({pct}%)</span>
                                        </div>
                                    );
                                })}
                        </div>

                        {/* Ratings */}
                        <div className="admin2-engagement-section">
                            <h4 className="admin2-engagement-subtitle">Ratings</h4>
                            {Object.keys(data.engagement.ratings).length === 0 ? (
                                <p className="admin2-muted" style={{ fontSize: '0.85rem' }}>No ratings yet</p>
                            ) : (
                                Object.entries(data.engagement.ratings)
                                    .sort(([a], [b]) => {
                                        const order = ['Wonderful', 'OK', 'Boring', 'Awful'];
                                        return order.indexOf(a) - order.indexOf(b);
                                    })
                                    .map(([label, count]) => {
                                        const total = Object.values(data.engagement.ratings).reduce((a, b) => a + b, 0);
                                        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                                        const colors = { 'Wonderful': '#22c55e', 'OK': '#3b82f6', 'Boring': '#f59e0b', 'Awful': '#ef4444' };
                                        const emojis = { 'Wonderful': '😃', 'OK': '🙂', 'Boring': '😐', 'Awful': '😡' };
                                        return (
                                            <div key={label} className="admin2-engagement-bar-row">
                                                <span className="admin2-engagement-label">{emojis[label] || ''} {label}</span>
                                                <div className="admin2-engagement-bar-bg">
                                                    <div
                                                        className="admin2-engagement-bar-fill"
                                                        style={{ width: `${pct}%`, backgroundColor: colors[label] || '#94a3b8' }}
                                                    />
                                                </div>
                                                <span className="admin2-engagement-count">{count} ({pct}%)</span>
                                            </div>
                                        );
                                    })
                            )}

                            {/* Checkin jobs status */}
                            <div className="admin2-engagement-jobs">
                                {data.jobs.midweekCheckin && (
                                    <div className="admin2-engagement-job-row">
                                        <span>Midweek check-in</span>
                                        <StatusBadge value={data.jobs.midweekCheckin.lastStatus} />
                                        {data.jobs.midweekCheckin.lastRun && <span className="admin2-muted">{formatTimeAgo(data.jobs.midweekCheckin.lastRun)}</span>}
                                    </div>
                                )}
                                {data.jobs.weekendFeedback && (
                                    <div className="admin2-engagement-job-row">
                                        <span>Weekend feedback</span>
                                        <StatusBadge value={data.jobs.weekendFeedback.lastStatus} />
                                        {data.jobs.weekendFeedback.lastRun && <span className="admin2-muted">{formatTimeAgo(data.jobs.weekendFeedback.lastRun)}</span>}
                                    </div>
                                )}
                                {data.jobs.weeklyCheckin && (
                                    <div className="admin2-engagement-job-row">
                                        <span>Weekly invitation</span>
                                        <StatusBadge value={data.jobs.weeklyCheckin.lastStatus} />
                                        {data.jobs.weeklyCheckin.lastRun && <span className="admin2-muted">{formatTimeAgo(data.jobs.weeklyCheckin.lastRun)}</span>}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Full-width: Current Matches Table */}
            <div className="admin2-card admin2-card-full">
                <h3 className="admin2-card-title">Current Matches</h3>
                {sortedMatches.length === 0 ? (
                    <p className="admin2-empty-state">No matches found for this week.</p>
                ) : (
                    <div className="admin2-table-wrapper">
                        <table className="admin2-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Member 1</th>
                                    <th>Member 2</th>
                                    <th>Pool</th>
                                    <th>Notifications</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedMatches.map((match, idx) => (
                                    <tr key={match.id}>
                                        <td className="admin2-table-num">{idx + 1}</td>
                                        <td>
                                            {match.member1?.name || '—'}
                                            {match.member1?.username && (
                                                <span className="admin2-muted"> (@{match.member1.username})</span>
                                            )}
                                        </td>
                                        <td>
                                            {match.member2?.name || '—'}
                                            {match.member2?.username && (
                                                <span className="admin2-muted"> (@{match.member2.username})</span>
                                            )}
                                        </td>
                                        <td>{match.pool || 'Global'}</td>
                                        <td><NotificationBadge value={match.notifications} /></td>
                                        <td><StatusBadge value={match.status} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Full-width: Script Runs */}
            <div className="admin2-card admin2-card-full">
                <h3 className="admin2-card-title">Script Runs (24h)</h3>
                {scriptRuns.length === 0 ? (
                    <p className="admin2-empty-state">No script runs in the last 24h</p>
                ) : (
                    <div className="admin2-timeline">
                        {scriptRuns.map((run, idx) => {
                            const isSuccess = run.status?.toUpperCase() === 'SUCCESS';
                            return (
                                <div key={idx} className="admin2-timeline-item">
                                    <span className={`admin2-timeline-dot ${isSuccess ? 'success' : 'error'}`} />
                                    <div className="admin2-timeline-body">
                                        <span className="admin2-timeline-script">{run.script}</span>
                                        <span className="admin2-timeline-meta">
                                            {formatTimeAgo(run.startedAt)}
                                            {run.duration && <> &middot; {run.duration}</>}
                                        </span>
                                        <StatusBadge value={run.status} />
                                        {run.metrics && (
                                            <span className="admin2-timeline-metrics">
                                                {run.metrics.candidates != null && run.metrics.matchesCreated != null
                                                    ? `${run.metrics.candidates} candidates → ${run.metrics.matchesCreated} matches`
                                                    : Object.entries(run.metrics)
                                                          .map(([k, v]) => `${k}: ${v}`)
                                                          .join(', ')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DashboardTab;
